import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './auth-context';
import { supabase } from './supabase';

interface AdminProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * A component that protects routes based on admin status
 * Only users with public.profile.is_admin = true can access these routes
 */
export function AdminProtectedRoute({ 
  children, 
  redirectTo = '/login'
}: AdminProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, profile } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsCheckingAdmin(false);
        return;
      }

      // First check if we already have the profile with admin status from auth context
      if (profile !== null) {
        setIsAdmin(profile.is_admin === true);
        setIsCheckingAdmin(false);
        return;
      }

      try {
        // Fallback: Query the profile table to check if the user is an admin
        const { data: profile, error } = await supabase
          .from('accounts')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking admin status:', error);
          // Try alternative method: use RPC function for admin check
          try {
            const { data: rpcData, error: rpcError } = await supabase
              .rpc('get_all_accounts_admin');
            
            if (!rpcError && rpcData && rpcData.length > 0) {
              // If RPC function works, user is admin
              setIsAdmin(true);
            } else {
              setIsAdmin(false);
            }
          } catch (rpcErr) {
            console.error('RPC admin check failed:', rpcErr);
            setIsAdmin(false);
          }
        } else {
          setIsAdmin(profile?.is_admin === true);
        }
      } catch (error) {
        console.error('Unexpected error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    if (!isLoading) {
      checkAdminStatus();
    }
  }, [user, profile, isLoading]);

  // Show loading state while checking auth or admin status
  if (isLoading || isCheckingAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="ml-4 text-muted-foreground">Checking admin access...</p>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Redirect if not an admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // If user is authenticated and is an admin, render the children
  return <>{children}</>;
}
